import { tokens } from '@tradelogic/ui-tokens';
import type { ReactNode } from 'react';

/**
 * Vendor portal branding theme tied to `@tradelogic/ui-tokens` (Q14).
 *
 * Produces a stable inline-style object that mirrors the admin console's
 * branding so the vendor portal renders identical colors/typography/spacing
 * derived from `t003_branding_configuration`. The styles are intentionally
 * exposed as a plain object so vitest can assert exact token propagation.
 */
export interface VendorBrandingStyles {
  readonly background: string;
  readonly surface: string;
  readonly textPrimary: string;
  readonly textSecondary: string;
  readonly brandPrimary: string;
  readonly brandAccent: string;
  readonly border: string;
  readonly fontFamilySans: string;
  readonly fontSizeBody: number;
  readonly spacingMd: number;
  readonly radiusMd: number;
  readonly shadowMd: string;
}

export const vendorBrandingStyles: VendorBrandingStyles = Object.freeze({
  background: tokens.color.semantic.background,
  surface: tokens.color.semantic.surface,
  textPrimary: tokens.color.semantic.textPrimary,
  textSecondary: tokens.color.semantic.textSecondary,
  brandPrimary: tokens.color.brand[600],
  brandAccent: tokens.color.brand[500],
  border: tokens.color.semantic.border,
  fontFamilySans: tokens.typography.fontFamily.sans,
  fontSizeBody: tokens.typography.fontSize.md,
  spacingMd: tokens.spacing.md,
  radiusMd: tokens.radii.md,
  shadowMd: tokens.shadows.md,
});

/**
 * Wraps the vendor portal shell with branding-derived styles. Anything
 * rendered inside inherits the same color, typography, and spacing tokens
 * as the admin console.
 */
export function VendorBrandingProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const style = {
    backgroundColor: vendorBrandingStyles.background,
    color: vendorBrandingStyles.textPrimary,
    fontFamily: vendorBrandingStyles.fontFamilySans,
    fontSize: vendorBrandingStyles.fontSizeBody,
    minHeight: '100vh',
  } as const;
  return (
    <div data-testid="vendor-branding-root" style={style}>
      {children}
    </div>
  );
}
